import React, { useState } from 'react';
import { API, C } from '../constants';

const AVATARS = ['🎬','🎭','🎪','🌟','🔥','🎯','🎸','🌙'];
const COLORS  = ['#c9714a','#5dade2','#27ae60','#9b59b6','#e67e22','#e74c3c','#1abc9c','#2980b9'];

function ProfileAvatar({ profile, size = 80 }) {
  const idx  = profile.id % COLORS.length;
  const char = profile.avatar || profile.name?.charAt(0)?.toUpperCase() || '?';
  const isEmoji = char.length > 1 || char.charCodeAt(0) > 127;
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background: isEmoji ? C.bgCard : COLORS[idx], display:'flex', alignItems:'center', justifyContent:'center', fontSize: isEmoji ? size*0.55+'px' : size*0.45+'px', color:'#fff', fontWeight:'700', border:`3px solid transparent`, flexShrink:0, fontFamily:'Inter, sans-serif' }}>
      {char}
    </div>
  );
}

export default function ProfileSelector({ token, profiles, onSelect, onProfilesChange }) {
  const [adding, setAdding]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName]       = useState('');
  const [avatar, setAvatar]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const createProfile = async () => {
    if (!name.trim()) { setError('Ingresa un nombre'); return; }
    setLoading(true); setError('');
    const res = await fetch(`${API}/api/profiles`, { method:'POST', headers, body: JSON.stringify({ name: name.trim(), avatar }) });
    const data = await res.json();
    if (!res.ok) { setError(data.error || 'Error'); setLoading(false); return; }
    onProfilesChange && onProfilesChange();
    setAdding(false); setName(''); setAvatar(''); setLoading(false);
  };

  const updateProfile = async () => {
    if (!name.trim()) { setError('Ingresa un nombre'); return; }
    setLoading(true); setError('');
    const res = await fetch(`${API}/api/profiles/${editing.id}`, { method:'PUT', headers, body: JSON.stringify({ name: name.trim(), avatar }) });
    if (!res.ok) { const d = await res.json(); setError(d.error || 'Error'); setLoading(false); return; }
    onProfilesChange && onProfilesChange();
    setEditing(null); setName(''); setAvatar(''); setLoading(false);
  };

  const deleteProfile = async (p) => {
    if (!window.confirm(`¿Eliminar perfil "${p.name}"? Se perderá su historial.`)) return;
    await fetch(`${API}/api/profiles/${p.id}`, { method:'DELETE', headers: { Authorization: `Bearer ${token}` } });
    onProfilesChange && onProfilesChange();
  };

  const startEdit = (p) => { setEditing(p); setName(p.name); setAvatar(p.avatar || ''); setAdding(false); setError(''); };
  const startAdd  = () => { setAdding(true); setEditing(null); setName(''); setAvatar(''); setError(''); };

  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'40px 20px' }}>
      <h1 style={{ fontFamily:'Playfair Display, serif', fontSize:'32px', color:C.accentSoft, letterSpacing:'2px', marginBottom:'8px' }}>TLALLI</h1>
      <p style={{ color:C.textMuted, fontSize:'14px', marginBottom:'48px', letterSpacing:'2px', textTransform:'uppercase' }}>¿Quién ve?</p>

      <div style={{ display:'flex', gap:'24px', flexWrap:'wrap', justifyContent:'center', marginBottom:'40px', maxWidth:'720px' }}>
        {profiles.map(p => (
          <div key={p.id} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px', cursor:'pointer', position:'relative' }}
            onClick={() => { if (!editing) onSelect(p); }}>
            <div style={{ position:'relative' }}>
              <div style={{ transition:'transform 0.15s', transform:'scale(1)' }}
                onMouseEnter={e => e.currentTarget.style.transform='scale(1.08)'}
                onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>
                <ProfileAvatar profile={p} size={96} />
              </div>
              <button onClick={e => { e.stopPropagation(); startEdit(p); }}
                style={{ position:'absolute', top:'-4px', right:'-4px', width:'22px', height:'22px', borderRadius:'50%', background:C.bgCard, border:`1px solid ${C.accentBorder}`, color:C.accentSoft, fontSize:'10px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
                ✏
              </button>
            </div>
            <span style={{ color:C.text, fontSize:'14px', fontWeight:'500', maxWidth:'96px', textAlign:'center', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'Inter, sans-serif' }}>{p.name}</span>
          </div>
        ))}

        {profiles.length < 5 && !adding && !editing && (
          <div onClick={startAdd} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px', cursor:'pointer' }}>
            <div style={{ width:96, height:96, borderRadius:'50%', background:'transparent', border:`2px dashed ${C.accentBorder}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'32px', color:C.accentBorder, transition:'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor=C.accent; e.currentTarget.style.color=C.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor=C.accentBorder; e.currentTarget.style.color=C.accentBorder; }}>
              +
            </div>
            <span style={{ color:C.textMuted, fontSize:'13px', fontFamily:'Inter, sans-serif' }}>Agregar</span>
          </div>
        )}
      </div>

      {(adding || editing) && (
        <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'16px', padding:'28px', width:'100%', maxWidth:'360px', marginBottom:'24px' }}>
          <h3 style={{ color:C.text, fontSize:'15px', fontWeight:'600', marginBottom:'18px', fontFamily:'Inter, sans-serif' }}>{adding ? 'Nuevo perfil' : `Editar "${editing?.name}"`}</h3>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Nombre del perfil"
            style={{ width:'100%', padding:'11px 14px', borderRadius:'8px', background:'rgba(255,255,255,0.04)', border:`1px solid ${C.accentBorder}`, color:C.text, fontSize:'14px', outline:'none', boxSizing:'border-box', marginBottom:'12px', fontFamily:'Inter, sans-serif' }} />
          <p style={{ color:C.textMuted, fontSize:'12px', marginBottom:'8px' }}>Avatar (emoji opcional)</p>
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'16px' }}>
            {AVATARS.map(em => (
              <span key={em} onClick={() => setAvatar(avatar === em ? '' : em)}
                style={{ fontSize:'22px', cursor:'pointer', padding:'4px', borderRadius:'6px', background: avatar === em ? C.accentDim : 'transparent', border:`1px solid ${avatar === em ? C.accentBorder : 'transparent'}`, transition:'all 0.1s' }}>
                {em}
              </span>
            ))}
          </div>
          {error && <p style={{ color:'#e07a5f', fontSize:'12px', marginBottom:'10px' }}>{error}</p>}
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={adding ? createProfile : updateProfile} disabled={loading}
              style={{ flex:1, padding:'9px', borderRadius:'8px', background: loading ? C.textDim : `linear-gradient(135deg, ${C.accent}, ${C.accentSoft})`, border:'none', color:'#fff', fontSize:'13px', fontWeight:'600', cursor: loading ? 'not-allowed' : 'pointer', fontFamily:'Inter, sans-serif' }}>
              {loading ? '...' : adding ? 'Crear' : 'Guardar'}
            </button>
            <button onClick={() => { setAdding(false); setEditing(null); setError(''); }}
              style={{ padding:'9px 14px', borderRadius:'8px', background:'transparent', border:`1px solid ${C.accentBorder}`, color:C.textMuted, fontSize:'13px', cursor:'pointer' }}>
              Cancelar
            </button>
            {editing && profiles.length > 1 && (
              <button onClick={() => deleteProfile(editing)}
                style={{ padding:'9px 14px', borderRadius:'8px', background:C.dangerDim, border:`1px solid rgba(192,57,43,0.3)`, color:C.danger, fontSize:'13px', cursor:'pointer' }}>
                Eliminar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
